import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { WhatsAppLogo } from "../components/BrandIcons";
import { getVehicleBySlug } from "../lib/data";
import { CONTACT } from "../lib/content";
import { QuoteForm } from "../components/QuoteForm";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "@/hooks/use-page-meta";
import { formatPrice, formatMonthlyKm } from "@/lib/price";

export default function VehicleDetail() {
  const { slug } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageMeta({ title: vehicle?.name, description: vehicle?.description, image: vehicle?.image_url });

  useEffect(() => {
    getVehicleBySlug(slug).then((v) => {
      setVehicle(v);
      setLoading(false);
    });
  }, [slug]);

  if (loading) {
    return (
      <div className="py-32 px-6 text-center text-[#6B6B6B] font-mono-lbl text-sm" data-testid="vehicle-loading">
        Araç bilgileri yükleniyor...
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="py-32 px-6 text-center" data-testid="vehicle-not-found">
        <h1 className="text-2xl font-serif-display text-[#404040]">Araç bulunamadı</h1>
        <Link to="/filo" className="text-[#8F7648] text-sm mt-4 inline-block hover:text-[#8F7648] transition-colors">
          Filoya dön
        </Link>
      </div>
    );
  }

  const specs = [
    { label: "Model Yılı", value: vehicle.year },
    { label: "Motor", value: vehicle.engine },
    { label: "Şanzıman", value: vehicle.transmission },
    { label: "Yakıt", value: vehicle.fuel },
    { label: "Koltuk", value: vehicle.seats },
    { label: "Renk", value: vehicle.color },
    { label: "Aylık Kullanım", value: formatMonthlyKm(vehicle.monthly_km) },
  ].filter((s) => s.value !== null && s.value !== undefined && s.value !== "");

  const waUrl = `${CONTACT.whatsapp}?text=${encodeURIComponent(
    `Merhaba, ${vehicle.name} aracı için teklif istiyorum.`
  )}`;
  const gallery = [vehicle.image_url, ...(vehicle.gallery || [])].filter(Boolean);

  return (
    <div className="py-16 px-6" data-testid="vehicle-detail-page">
      <div className="max-w-6xl mx-auto">
        <Link
          to="/filo"
          data-testid="vehicle-back-link"
          className="inline-flex items-center gap-2 text-xs font-mono-lbl uppercase tracking-widest text-[#6B6B6B] hover:text-[#B09060] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Filoya Dön
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-8">
          <Reveal>
            <div className="space-y-4">
              {gallery.map((src, i) => (
                <div
                  key={i}
                  className="rounded-sm overflow-hidden border border-[#B09060]/20 h-72 md:h-96"
                >
                  <img src={src} alt={`${vehicle.name} ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div>
              {vehicle.badge && (
                <span className="inline-block px-3 py-1 rounded border border-[#B09060]/40 bg-[#B09060]/10 text-[#8F7648] text-xs font-mono-lbl uppercase tracking-widest">
                  {vehicle.badge}
                </span>
              )}
              <div className="text-xs font-mono-lbl uppercase tracking-widest text-[#6B6B6B] mt-4">
                {vehicle.segment}
              </div>
              <h1 className="text-3xl md:text-5xl font-serif-display text-[#404040] mt-2" data-testid="vehicle-name">
                {vehicle.name}
              </h1>
              <div className="text-sm text-[#6B6B6B] mt-1 font-mono-lbl">
                {[vehicle.year, vehicle.color].filter(Boolean).join(" · ")}
              </div>

              <div className="mt-6 flex items-end gap-2">
                <span className="text-4xl font-bold text-[#8F7648]" data-testid="vehicle-price">
                  {formatPrice(vehicle.price_label)}
                </span>
                <span className="text-xs text-[#6B6B6B] mb-1.5">+ KDV / Ay</span>
              </div>
              {formatMonthlyKm(vehicle.monthly_km) && (
                <div className="text-sm text-[#6B6B6B] mt-2" data-testid="vehicle-monthly-km">
                  Aylık kullanım: <span className="font-semibold text-[#404040]">{formatMonthlyKm(vehicle.monthly_km)}</span>
                </div>
              )}

              <p className="text-[#555555] text-sm leading-relaxed mt-6">
                {vehicle.description}
              </p>

              <div className="grid grid-cols-2 gap-px bg-[#B09060]/20 [&>*:last-child:nth-child(odd)]:col-span-2 rounded-sm overflow-hidden border border-[#B09060]/20 mt-8" data-testid="vehicle-specs">
                {specs.map((s) => (
                  <div key={s.label} className="bg-white p-4">
                    <div className="text-[10px] font-mono-lbl uppercase tracking-widest text-[#9B9B8F]">
                      {s.label}
                    </div>
                    <div className="text-sm text-[#404040] mt-1 font-medium">{s.value}</div>
                  </div>
                ))}
              </div>

              {vehicle.features && vehicle.features.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-xs font-mono-lbl uppercase tracking-widest text-[#8F7648] mb-4">
                    Öne Çıkan Donanımlar
                  </h3>
                  <ul className="space-y-2.5" data-testid="vehicle-features">
                    {vehicle.features.map((f) => (
                      <li key={f} className="flex items-center gap-3 text-sm text-[#555555]">
                        <Check className="w-4 h-4 text-[#8F7648] shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="#vehicle-quote"
                  data-testid="vehicle-quote-cta"
                  className="flex-1 text-center py-3.5 px-6 rounded bg-[#B09060] text-[#FAFBF6] font-bold text-xs uppercase tracking-wider hover:bg-[#8F7648] transition-colors shadow-[0_4px_20px_rgba(176,144,96,0.25)]"
                >
                  Teklif İste
                </a>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="vehicle-whatsapp-cta"
                  className="flex items-center justify-center gap-2 py-3.5 px-6 rounded border border-[#25D366]/40 bg-[#25D366]/10 text-[#128C4B] font-bold text-xs uppercase tracking-wider hover:bg-[#25D366]/20 transition-colors"
                >
                  <WhatsAppLogo className="w-5 h-5" /> WhatsApp
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <div id="vehicle-quote" className="max-w-2xl mx-auto mt-24">
          <Reveal>
            <h2 className="text-2xl md:text-4xl font-serif-display text-[#404040] text-center">
              {vehicle.name} için Teklif Alın
            </h2>
            <div className="mt-8">
              <QuoteForm formType="teklif" defaultVehicle={vehicle.name} />
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
